import React, { useCallback, memo } from "react";
import { BlockStack, Button, InlineStack, List, Text, Icon } from "@shopify/polaris";
import { ExternalIcon, RefreshIcon, CheckCircleIcon } from "@shopify/polaris-icons";
import { openRemoteUrl } from "../../utils/openRemoteUrl";
import { APP_DISPLAY_NAME } from "../../config/appBranding";

/**
 * Shown when the setup guide is dismissed: same card for embed off/on; primary CTA opens the theme editor.
 * Refresh uses themeRefreshing so the card stays mounted (no full-banner loading).
 */
function ThemeAppEmbedStatusBanner({
  themeLoading,
  themeRefreshing,
  embedInactive,
  themeCheckError,
  onRefreshThemes,
  getThemeEditorDeepLink,
}) {
  const onThemeEditor = useCallback(() => {
    const url = getThemeEditorDeepLink?.();
    openRemoteUrl(url, { newContext: true });
    window.setTimeout(() => {
      onRefreshThemes?.();
    }, 2000);
  }, [getThemeEditorDeepLink, onRefreshThemes]);

  const refreshBusy = themeLoading || themeRefreshing;
  const inactive = embedInactive;

  return (
    <div className={`age-embed-callout${inactive ? "" : " age-embed-callout--embed-on"}`}>
      <div
        className={`age-embed-callout__header${inactive ? "" : " age-embed-callout__header--active"}`}
      >
        <span className="age-embed-callout__headerIcon" aria-hidden>
          {inactive ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10 4L17 16H3L10 4Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
                fill="none"
              />
              <path d="M10 8v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="10" cy="14" r="0.75" fill="currentColor" />
            </svg>
          ) : (
            <Icon source={CheckCircleIcon} tone="base" />
          )}
        </span>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {inactive
            ? `The ${APP_DISPLAY_NAME} app embed is not active in your live theme`
            : `The ${APP_DISPLAY_NAME} app embed is active in your live theme`}
        </Text>
      </div>
      <div className="age-embed-callout__body">
        <BlockStack gap="400">
          <List type="bullet">
            <List.Item>
              <Text as="span" variant="bodyMd">
                {inactive ? (
                  <>
                    The calendar is hidden on the storefront until you enable this app&apos;s embed in the theme
                    editor.
                  </>
                ) : (
                  <>
                    The calendar is visible on the storefront. Open the theme editor to change where this app&apos;s
                    embed appears.
                  </>
                )}
              </Text>
            </List.Item>
          </List>
          {themeCheckError ? (
            <Text as="p" variant="bodySm" tone="critical">
              Theme check: {themeCheckError}
            </Text>
          ) : null}
          <InlineStack gap="300" wrap>
            <Button
              variant="primary"
              icon={ExternalIcon}
              onClick={onThemeEditor}
              disabled={themeLoading}
            >
              {inactive ? "Enable app" : "Disable app"}
            </Button>
            <Button
              variant="secondary"
              icon={RefreshIcon}
              onClick={() => void onRefreshThemes?.()}
              loading={refreshBusy}
              disabled={refreshBusy}
            >
              Refresh status
            </Button>
          </InlineStack>
        </BlockStack>
      </div>
    </div>
  );
}

export default memo(ThemeAppEmbedStatusBanner);
